import { Check, X } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useT } from "@/lib/i18n";
import type { ScenarioRule } from "@/lib/scenarios";
import { cn } from "@/lib/utils";
import { RulePopup } from "../RulePopup";

type RuleStepProps = {
  rule: ScenarioRule;
  onComplete: () => void;
};

/**
 * RULE step — read the rule, then (if the author provided any) answer
 * 2–3 inline MCQ examples before advancing. Per the new pedagogy:
 * sessions are study + light practice, so this is where "did it land"
 * happens. Real production happens at the checkpoint.
 *
 * Three rendered phases per call:
 *   1. READ — title + summary + escape hatch to full-rule popup.
 *      "Понятно — дальше" if no examples; "К проверке" if examples > 0.
 *   2. PRACTICE — one MCQ at a time. Selecting an option colors it
 *      correct/wrong; correct AND wrong reveal the right answer.
 *      "Дальше" advances within the example set.
 *   3. DONE — final "Дальше" hands control back to the SessionController.
 *
 * Mockup: `design/hifi/scenarios.jsx` `ScenarioRunScreen` step 4.
 */
export function RuleStep({ rule, onComplete }: RuleStepProps) {
  const t = useT();
  // Full-screen rule reader — no-side-effects read-only surface.
  const [readerOpen, setReaderOpen] = useState(false);

  const hasExamples = rule.examples?.length > 0;

  // PRACTICE state. `phase: "read"` shows the rule body; `practice`
  // walks through `examples[]`. `cursor` indexes into rule.examples;
  // `picked` is the option string the user clicked (null = not yet
  // answered for the current item).
  const [phase, setPhase] = useState<"read" | "practice">("read");
  const [cursor, setCursor] = useState(0);
  const [picked, setPicked] = useState<string | null>(null);

  const example = rule.examples?.[cursor];
  const isLastExample = cursor === rule.examples?.length - 1;

  const onPickOption = (opt: string) => {
    if (picked) return;
    setPicked(opt);
  };

  const onNextExample = () => {
    if (!example) return;
    if (isLastExample) {
      onComplete();
      return;
    }
    setCursor(cursor + 1);
    setPicked(null);
  };

  const onStartPractice = () => {
    if (hasExamples) setPhase("practice");
    else onComplete();
  };

  return (
    <div className="flex flex-1 flex-col gap-4">
      {phase === "read" ? (
        <>
          <div className="flashcard flex-1 overflow-y-auto">
            <div className="font-mono text-[11px] font-semibold uppercase tracking-wider text-[color:var(--scen-fg)]">
              {t("skills.detail_rules")}
            </div>
            <h2 className="text-[24px] font-bold leading-[1.15] tracking-[-0.02em] text-text">
              {rule.title ?? rule.slug}
            </h2>
            {rule.summary && (
              <>
                <div className="mt-2 font-mono text-[11px] uppercase tracking-wider text-text-3">
                  {t("skills.session_rule_summary")}
                </div>
                <p className="text-[15px] leading-[1.5] text-text-2">{rule.summary}</p>
              </>
            )}
            <button
              type="button"
              onClick={() => setReaderOpen(true)}
              className="mt-3 inline-flex items-center gap-1 text-[13px] text-[color:var(--scen-fg)] underline-offset-2 hover:underline"
            >
              {t("skills.session_open_full")} →
            </button>
          </div>
          <Button variant="scenario" size="block" onClick={onStartPractice}>
            {hasExamples ? t("skills.session_to_practice") : t("skills.session_understood")}
          </Button>
        </>
      ) : (
        example && (
          <>
            <div className="flashcard flex-1 overflow-y-auto">
              <div className="flex items-baseline justify-between">
                <div className="font-mono text-[11px] font-semibold uppercase tracking-wider text-[color:var(--scen-fg)]">
                  {t("skills.session_practice_label")}
                </div>
                <div className="font-mono text-[11px] text-text-3">
                  {cursor + 1} / {rule.examples.length}
                </div>
              </div>
              <p className="mt-2 text-[18px] leading-[1.4] text-text">{example.prompt}</p>
              <div className="mt-3 flex flex-col gap-2">
                {example.options.map((opt, i) => {
                  const letter = String.fromCharCode(65 + i);
                  const isCorrect = opt === example.answer;
                  const status: "idle" | "correct" | "wrong" =
                    picked === null ? "idle" : isCorrect ? "correct" : picked === opt ? "wrong" : "idle";
                  return (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => onPickOption(opt)}
                      disabled={picked !== null}
                      className={cn(
                        "flex items-center gap-3 rounded-[var(--r-md)] border-[1.5px] px-3.5 py-3 text-left text-[15px] font-medium transition-colors",
                        status === "idle" && "border-border bg-surface hover:bg-[color:var(--surface-2)]",
                        status === "correct" &&
                          "border-[color:var(--good)] bg-[color:var(--good-soft)] text-[color:var(--good)]",
                        status === "wrong" &&
                          "border-[color:var(--again)] bg-[color:var(--again-soft)] text-[color:var(--again)]",
                      )}
                    >
                      <span className="flex size-6 items-center justify-center rounded-full border-[1.5px] border-current text-[12px] font-semibold">
                        {letter}
                      </span>
                      <span className="flex-1">{opt}</span>
                      {status === "correct" && <Check className="size-4" />}
                      {status === "wrong" && <X className="size-4" />}
                    </button>
                  );
                })}
              </div>
            </div>
            {picked !== null && (
              <Button variant="scenario" size="block" onClick={onNextExample}>
                {isLastExample ? t("skills.session_continue") : t("skills.session_next")}
              </Button>
            )}
          </>
        )
      )}

      <RulePopup slugOrId={readerOpen ? rule.slug : null} onClose={() => setReaderOpen(false)} />
    </div>
  );
}
